/**
 * What may be uploaded as a referral, and how it is described to the operator.
 *
 * Kept in step with `REFERRAL_UPLOAD_EXTENSION_BY_MIME_TYPE` in leads-api: the API rejects any
 * type it does not know, so a file the picker accepts but the API refuses would fail after the
 * operator had already chosen it.
 *
 * The browser is not a reliable source of MIME types — Windows reports a ZIP as
 * `application/x-zip-compressed`, and some browsers report an empty type for `.zip` and `.xml`
 * entirely — so the extension is checked as well and is what decides the type we declare.
 */
export const REFERRAL_UPLOAD_TYPES: Array<{
  extension: string;
  mimeType: string;
  /** Types a browser may report for this extension, including the empty string. */
  reportedMimeTypes: string[];
}> = [
  {
    extension: '.pdf',
    mimeType: 'application/pdf',
    reportedMimeTypes: ['application/pdf'],
  },
  {
    extension: '.zip',
    mimeType: 'application/zip',
    reportedMimeTypes: ['application/zip', 'application/x-zip-compressed', 'multipart/x-zip', ''],
  },
  {
    extension: '.xml',
    mimeType: 'text/xml',
    reportedMimeTypes: ['text/xml', 'application/xml', ''],
  },
];

/** The `accept` attribute for the file input: extensions, since MIME types are unreliable. */
export const REFERRAL_UPLOAD_ACCEPT = REFERRAL_UPLOAD_TYPES.map((type) => type.extension).join(
  ',',
);

export const REFERRAL_UPLOAD_HINT = 'PDF, or a ZIP/XML referral package';

/**
 * The MIME type to declare for a chosen file, or null when it is not a referral document.
 *
 * Decided by extension first. A browser that reports nothing for a `.zip` is common enough
 * that trusting `file.type` alone would reject a valid package with a message the operator
 * cannot act on.
 */
export const resolveReferralUploadType = (file: File): string | null => {
  const name = (file.name ?? '').toLowerCase();
  const byExtension = REFERRAL_UPLOAD_TYPES.find((type) => name.endsWith(type.extension));

  if (byExtension) {
    return byExtension.mimeType;
  }

  const byReportedType = REFERRAL_UPLOAD_TYPES.find((type) =>
    type.reportedMimeTypes.includes(file.type),
  );

  return byReportedType && file.type ? byReportedType.mimeType : null;
};

/**
 * Whether a structured package will be parsed rather than read as a scanned document.
 *
 * Used only to tell the operator what will happen, so an XML upload that produces no page
 * preview is expected rather than alarming.
 */
export const isStructuredReferralUpload = (file: File): boolean => {
  const name = (file.name ?? '').toLowerCase();
  return name.endsWith('.zip') || name.endsWith('.xml');
};

/**
 * Whether a stored document is a PDF, judged by its key.
 *
 * Presigned URLs carry a query string, so it is stripped before the extension is read; and a
 * missing key is not a PDF. Used to keep anything else — ZIP, and particularly HL7 XML from an
 * external sender — out of the inline preview iframe.
 */
export const isPdfDocumentKey = (key: string | null | undefined): boolean =>
  // Both the query and the fragment are cut: `evil.xml#x.pdf` must not pass as a PDF.
  !!key && key.split(/[?#]/)[0].toLowerCase().endsWith('.pdf');

/**
 * The URL a stored document opens from. `s3Key` arrives either as a full (presigned) URL or as
 * a bare `bucket/key` path, which gets a scheme. A missing key has no URL.
 */
export const buildDocumentUrl = (key: string | null | undefined): string | null =>
  key ? (key.startsWith('http') ? key : `https://${key}`) : null;

/**
 * How a stored document is shown: only a PDF is previewed inline. Anything else (a partner's
 * ZIP or HL7 XML) opens as a download, because an XML carrying an `<?xml-stylesheet?>` executes
 * its XSLT when a browser renders it. `null` when the document has no file.
 */
export const resolveDocumentFile = (
  key: string | null | undefined,
): { url: string; isPdf: boolean } | null => {
  const url = buildDocumentUrl(key);
  return url ? { url, isPdf: isPdfDocumentKey(key) } : null;
};

/** Opens a document that is not previewed inline, in a tab that cannot reach back into this one. */
export const openDocumentAsDownload = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};
